import { useEffect, useState } from 'react';
import { KeyRound, Save, X, Edit2, Trash2, Plus } from 'lucide-react';
import { accessCodesAPI } from '../services/api.js';

// Access-code admin (346 DTMF feature). A tenant admin defines short DTMF codes a
// caller punches at the START of a call; each code maps to an ACTION the agent
// runs instead of the normal panel discovery flow:
//
//   notes        → the AI speaks the configured notes text, then wraps the call.
//   expert_mode  → the LIVE AI switches persona to an expert (no dial).
//   join_meet    → the AI bridges the caller into a Google Meet via the meeting's
//                  PSTN "Join by phone" dial-in + PIN (dialed into the room + DTMF'd).
//   join_call    → the AI dials a number INTO the call, making it a conference —
//                  a bridge line, a colleague, whoever should be on it.
//
// Mirrors PhoneNumbersEditor / FaqEditor: tenant-scoped list + inline create/edit,
// admin-gated on the server. The payload fields shown depend on the action type
// (matching the per-action payload schemas in accessCodeService.js).

// Action types OFFERED in the create/edit dropdown.
//
// ⛔ 'join_call' WAS REMOVED FROM HERE AND THAT LEFT NO WAY TO SAY "CONFERENCE".
//
// It was dropped as "legacy" once join_meet existed — but join_meet is Google Meet
// specifically, and the two remaining options do not dial anything at all. Brian,
// 2026-09-01, wanting a code that puts the AI on a conference call: "These are my
// only options... to provide any information, I have to select expert mode." He was
// right, and the result was a code saved as a persona switch that dials nobody.
//
// Nothing was ever wrong with join_call — the backend validates it, the agent runs
// it, and every field and validation rule for it survived in this file. Only the
// dropdown entry was missing. It is back, named for what it does rather than for
// the mechanism.
const ACTION_TYPES = [
  { value: 'notes', label: 'Speak notes' },
  { value: 'expert_mode', label: 'Expert mode' },
  { value: 'join_call', label: 'Join a conference / bridge a number' },
  { value: 'join_meet', label: 'Join Google Meet' }
];

// A blank draft for the create form / starting an edit.
function emptyDraft() {
  return {
    code: '', label: '', actionType: 'notes', enabled: true,
    text: '', targetE164: '', displayName: '',
    expertPrompt: '', ackText: '',
    meetDialIn: '', meetPin: ''
  };
}

// Flatten a server row into the flat draft the form edits.
function rowToDraft(row) {
  const p = row.actionPayload || {};
  return {
    code: row.code || '',
    label: row.label || '',
    actionType: row.actionType || 'notes',
    enabled: row.enabled ?? true,
    text: p.text || '',
    targetE164: p.targetE164 || '',
    displayName: p.displayName || '',
    expertPrompt: p.expertPrompt || '',
    ackText: p.ackText || '',
    meetDialIn: p.meetDialIn || '',
    meetPin: p.meetPin || ''
  };
}

// Build the API payload (with the nested actionPayload) from a flat draft.
function draftToPayload(d) {
  let actionPayload;
  if (d.actionType === 'join_call') {
    // Legacy type — not offered in the UI, but kept for completeness/safety.
    actionPayload = {
      targetE164: d.targetE164.trim(),
      ...(d.displayName.trim() ? { displayName: d.displayName.trim() } : {})
    };
  } else if (d.actionType === 'expert_mode') {
    // Both fields optional (the agent has sensible defaults) — omit when blank.
    actionPayload = {
      ...(d.expertPrompt.trim() ? { expertPrompt: d.expertPrompt.trim() } : {}),
      ...(d.ackText.trim() ? { ackText: d.ackText.trim() } : {})
    };
  } else if (d.actionType === 'join_meet') {
    // Both required: the Meet dial-in (E.164) and the meeting PIN.
    actionPayload = {
      meetDialIn: d.meetDialIn.trim(),
      meetPin: d.meetPin.trim()
    };
  } else {
    actionPayload = { text: d.text.trim() };
  }
  return {
    code: d.code.trim(),
    label: d.label.trim(),
    actionType: d.actionType,
    enabled: !!d.enabled,
    actionPayload
  };
}

// Short human description of what a code does, for the list row.
function describeAction(row) {
  const p = row.actionPayload || {};
  if (row.actionType === 'expert_mode') {
    const ack = (p.ackText || '').trim();
    return ack ? `Expert mode — "${ack.length > 50 ? `${ack.slice(0, 50)}…` : ack}"` : 'Expert mode';
  }
  if (row.actionType === 'join_meet') {
    return `Join Google Meet (${p.meetDialIn || '—'})`;
  }
  if (row.actionType === 'join_call') {
    return `Join ${p.displayName ? `${p.displayName} ` : ''}(${p.targetE164 || '—'})`;
  }
  if (row.actionType === 'notes') {
    const t = (p.text || '').trim();
    return t.length > 60 ? `Notes: ${t.slice(0, 60)}…` : `Notes: ${t || '—'}`;
  }
  return row.actionType;
}

// Shared field block for both create + edit forms.
function CodeFields({ draft, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          className="field-input"
          placeholder="Code (digits, e.g. 1234)"
          value={draft.code}
          onChange={(e) => set({ ...draft, code: e.target.value })}
          maxLength={8}
        />
        <input
          className="field-input"
          placeholder="Label (e.g. After-hours nurse)"
          value={draft.label}
          onChange={(e) => set({ ...draft, label: e.target.value })}
          maxLength={120}
        />
      </div>

      <select
        className="field-input"
        value={draft.actionType}
        onChange={(e) => set({ ...draft, actionType: e.target.value })}
      >
        {ACTION_TYPES.map((a) => (
          <option key={a.value} value={a.value}>{a.label}</option>
        ))}
      </select>

      {draft.actionType === 'notes' && (
        <textarea
          className="field-input min-h-[80px]"
          placeholder="Notes the AI speaks when this code is entered."
          value={draft.text}
          onChange={(e) => set({ ...draft, text: e.target.value })}
          maxLength={4000}
        />
      )}

      {draft.actionType === 'expert_mode' && (
        <div className="space-y-3">
          <textarea
            className="field-input min-h-[80px]"
            placeholder="Expert prompt — the persona/mindset the AI switches into (optional; the agent has a sensible default)."
            value={draft.expertPrompt}
            onChange={(e) => set({ ...draft, expertPrompt: e.target.value })}
            maxLength={4000}
          />
          <textarea
            className="field-input min-h-[60px]"
            placeholder="Spoken acknowledgement — what the AI says when the code is entered (optional)."
            value={draft.ackText}
            onChange={(e) => set({ ...draft, ackText: e.target.value })}
            maxLength={1000}
          />
        </div>
      )}

      {draft.actionType === 'join_meet' && (
        <div className="space-y-3">
          <p className="text-xs text-stone-500">
            Open the Google Meet, click <span className="text-stone-300">Joining info</span>,
            and copy the phone number + PIN from "Join by phone".
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              className="field-input"
              placeholder="Meet dial-in number (+14134892965)"
              value={draft.meetDialIn}
              onChange={(e) => set({ ...draft, meetDialIn: e.target.value })}
            />
            <input
              className="field-input"
              placeholder="Meet PIN (e.g. 789 613 622)"
              value={draft.meetPin}
              onChange={(e) => set({ ...draft, meetPin: e.target.value })}
              maxLength={40}
            />
          </div>
        </div>
      )}

      {/* The conference option: a number dialed INTO the call. */}
      {draft.actionType === 'join_call' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="field-input"
            placeholder="Number to dial (+13462580006)"
            value={draft.targetE164}
            onChange={(e) => set({ ...draft, targetE164: e.target.value })}
          />
          <input
            className="field-input"
            placeholder="Who's joining (optional, e.g. On-call nurse)"
            value={draft.displayName}
            onChange={(e) => set({ ...draft, displayName: e.target.value })}
            maxLength={120}
          />
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-stone-300">
        <input
          type="checkbox"
          checked={!!draft.enabled}
          onChange={(e) => set({ ...draft, enabled: e.target.checked })}
        />
        Enabled
      </label>
    </div>
  );
}

export default function AccessCodesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [creating, setCreating] = useState(null); // draft | null
  const [editing, setEditing] = useState({}); // id -> draft

  const load = async () => {
    setLoading(true);
    try {
      const r = await accessCodesAPI.list();
      setItems(r?.data || r || []);
    } catch (err) {
      setError(err.message || 'Failed to load access codes.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const validate = (d) => {
    if (!/^[0-9*#]{2,8}$/.test(d.code.trim())) return 'Code must be 2–8 digits (or * / #).';
    if (!d.label.trim()) return 'Label is required.';
    if (d.actionType === 'notes' && !d.text.trim()) return 'Notes text is required.';
    if (d.actionType === 'join_call' && !/^\+[1-9]\d{6,14}$/.test(d.targetE164.trim())) {
      return 'Enter the number to dial in E.164 format (e.g. +13462580006).';
    }
    if (d.actionType === 'join_meet') {
      if (!/^\+[1-9]\d{6,14}$/.test(d.meetDialIn.trim())) {
        return 'Enter the Meet dial-in number in E.164 format (e.g. +14134892965).';
      }
      if (!/^[0-9*# -]{2,40}$/.test(d.meetPin.trim())) {
        return 'Enter the Meet PIN (digits; spaces/dashes allowed, e.g. 789 613 622).';
      }
    }
    // expert_mode: both fields are optional (the agent has sensible defaults).
    return null;
  };

  const create = async () => {
    const msg = validate(creating);
    if (msg) { setError(msg); return; }
    try {
      const r = await accessCodesAPI.create(draftToPayload(creating));
      setItems((list) => [r.data, ...list]);
      setCreating(null);
      setSavedAt(new Date());
      setError('');
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  const startEdit = (row) => setEditing((s) => ({ ...s, [row.id]: rowToDraft(row) }));
  const cancelEdit = (id) => setEditing((s) => { const n = { ...s }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    const msg = validate(draft);
    if (msg) { setError(msg); return; }
    try {
      const r = await accessCodesAPI.update(id, draftToPayload(draft));
      setItems((list) => list.map((p) => (p.id === id ? r.data : p)));
      cancelEdit(id);
      setSavedAt(new Date());
      setError('');
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this access code? Callers using it will fall back to the normal flow.')) return;
    try {
      await accessCodesAPI.remove(id);
      setItems((list) => list.filter((p) => p.id !== id));
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  return (
    <section className="card">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Access codes</h2>
        </div>
        {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
      </header>

      <p className="text-sm text-stone-400 mb-4">
        Callers can punch a short code at the start of a call to switch what the
        assistant does — speak a set of notes, switch the AI into expert mode, bring
        another number onto the call, or bridge the caller into a Google Meet. Leave
        this empty and every call runs the normal flow.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {!creating && (
        <button type="button" className="btn-ghost mb-4" onClick={() => { setCreating(emptyDraft()); setError(''); }}>
          <Plus size={14} /> Add code
        </button>
      )}

      {creating && (
        <div className="mb-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
          <CodeFields draft={creating} set={setCreating} />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setCreating(null); setError(''); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No access codes yet.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((row) => {
            const isEditing = !!editing[row.id];
            return (
              <li key={row.id} className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-stone-100 font-medium">
                      <span className="font-mono tracking-widest text-alfred-gold">{row.code}</span>
                      <span className="ml-2">{row.label}</span>
                      {!row.enabled && <span className="ml-2 text-xs text-stone-500 italic">— disabled</span>}
                    </div>
                    <div className="text-xs text-stone-500">{describeAction(row)}</div>
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" className="btn-ghost" onClick={() => startEdit(row)}>
                        <Edit2 size={14} /> Edit
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => remove(row.id)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="mt-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
                    <CodeFields draft={editing[row.id]} set={(d) => setEditing((s) => ({ ...s, [row.id]: d }))} />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={() => saveEdit(row.id)}>
                        <Save size={14} /> Save
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => cancelEdit(row.id)}>
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
